import { useEffect, useRef } from 'react'

import type { LogSensor } from '../api'
import { FORSTYRRET_DB, HOERER_HI_HZ, HOERER_LO_HZ, STERK_DB, hoertAvvikDb }
  from '../laks'
import { type Playhead } from '../playhead'

/** Timen slik den kjennes for en laks.
 *
 * ---------------------------------------------------------------------------
 * HVORFOR DENNE FINNES
 *
 * Du trykker på lytt og hører vann. Det er ikke en feil i opptaket: det er
 * øret ditt. Menneskehørselen faller bratt av under omtrent 100 Hz, mens
 * laksen hører best mellom 100 og 160 Hz og ingenting over 400. En skipstone
 * på 32 Hz kan ligge 30 dB over det vanlige på hydrofonen og være borte i en
 * laptophøyttaler, mens regnet på 8 kHz som fyller høyttaleren ikke finnes for
 * fisken i det hele tatt. Spekteret ved siden av sier dette med tall og
 * søyler. Denne ruta sier det uten tall.
 *
 * ---------------------------------------------------------------------------
 * HVA SOM STYRER FORMEN
 *
 * Én størrelse: avviket fra det vanlige, vektet med artens audiogram. Den
 * stiplede sirkelen er «vanlig» for dette stedet og denne timen. Blobben er
 * timen. Ligger den innenfor sirkelen var det en rolig time; svulmer den
 * utenfor, var det ikke det -- og hvor mye den svulmer er hvor mye.
 *
 * Tersklene er hvilescorens egne (6 dB og 12 dB, `analyzer/domain/rest.py`), så
 * blobben og ringen øverst på siden aldri sier hver sin ting om samme natt.
 *
 * Mens et klipp spiller legges lyden selv oppå: en analysator med båndpass på
 * 30-400 Hz driver skjelvingen, så det du ser rykke er det fisken ville kjent
 * rykke -- og det rykker fortsatt når høyttaleren er stille.
 *
 * Formen er organisk med vilje. En søyle er et måltall; en form som puster er
 * et dyr. Det er den ene figuren på siden som skal treffe før den leses.
 */

/** Hvor mange punkter blobben er bygget av. Åtte er nok til å lese som en
 *  organisme og få nok til at ingen enkelt bule blir en pigg. */
const N = 9

/** Faste faseforskyvninger. Ikke tilfeldige: en blob som ser forskjellig ut ved
 *  hver montering leser som støy framfor som en avlesning. */
const FASE = [0, 2.4, 4.1, 1.2, 5.3, 3.0, 0.7, 4.8, 2.0]

/** Lukket kurve gjennom punktene, som Catmull-Rom oversatt til kubiske
 *  Bézier-segmenter. Rett linje mellom punktene gir en polygon, og en polygon
 *  er en form ingen kjenner igjen som levende. */
function kurve(pts: [number, number][]): string {
  const n = pts.length
  let d = `M${pts[0]![0].toFixed(2)},${pts[0]![1].toFixed(2)}`
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n]!
    const p1 = pts[i]!
    const p2 = pts[(i + 1) % n]!
    const p3 = pts[(i + 2) % n]!
    d += `C${(p1[0] + (p2[0] - p0[0]) / 6).toFixed(2)},${(p1[1] + (p2[1] - p0[1]) / 6).toFixed(2)}`
      + ` ${(p2[0] - (p3[0] - p1[0]) / 6).toFixed(2)},${(p2[1] - (p3[1] - p1[1]) / 6).toFixed(2)}`
      + ` ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`
  }
  return `${d}Z`
}

export function Lydblob({ sensor, ph, tapp }: {
  sensor: LogSensor
  ph?: Playhead
  /** Meldes til når spilleren har en kilde å tappe. Blobben tar imot noden og
   *  bygger sitt eget båndpass på den; spilleren vet ingenting om hva den
   *  brukes til. */
  tapp?: (f: (ctx: AudioContext, kilde: AudioNode) => void) => void
}) {
  const bane = useRef<SVGPathElement>(null)
  const rms = useRef(0)
  const analyse = useRef<AnalyserNode | null>(null)
  const buf = useRef<Float32Array | null>(null)

  const avvik = hoertAvvikDb(sensor.band_centres_hz ?? [], sensor.band_excess_db ?? [])

  /* 0 ved «som vanlig», 1 ved 20 dB over. Over det metter den: forskjellen på
     22 og 30 dB er ikke noe en form kan bære, og et tall som vokser i det
     uendelige gjør en figur til en graf. */
  const uro = avvik === null ? 0 : Math.max(0, Math.min(1, avvik / 20))

  const klasse = avvik === null ? 'ukjent'
    : avvik >= STERK_DB ? 'sterk' : avvik >= FORSTYRRET_DB ? 'uro' : 'rolig'

  /* Båndpasset. To biquad-ledd framfor ett «bandpass»-ledd, fordi 30-400 Hz er
     nesten fire oktaver -- en enkel bandpass med den bredden har en Q under en
     halv og slipper gjennom mesteparten av det den skulle stenge ute. */
  useEffect(() => {
    if (!tapp) return
    tapp((ctx, kilde) => {
      const hp = ctx.createBiquadFilter()
      hp.type = 'highpass'; hp.frequency.value = HOERER_LO_HZ; hp.Q.value = 0.7
      const lp = ctx.createBiquadFilter()
      lp.type = 'lowpass'; lp.frequency.value = HOERER_HI_HZ; lp.Q.value = 0.7
      const a = ctx.createAnalyser()
      a.fftSize = 2048
      /* Kilden går hit i PARALLELL med lyttekjeden, og analysatoren går ikke
         videre til noen destinasjon. En gren som ender i en analysator lager
         ingen lyd, som er hele poenget: dette skal måle klippet, ikke legge et
         båndpasset ekko oppå det. */
      kilde.connect(hp); hp.connect(lp); lp.connect(a)
      analyse.current = a
      buf.current = new Float32Array(a.fftSize)
    })
  }, [tapp])

  useEffect(() => {
    const el = bane.current
    if (!el) return
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const punkt = (t: number, ekstra: number): [number, number][] => {
      const ut: [number, number][] = []
      /* Grunnradien vokser med uroen. En urolig time er ikke bare mer
         skjelven, den er STØRRE -- volum leses før tekstur. */
      const R = 30 * (1 + 0.34 * uro) * (1 + 0.02 * Math.sin(t * 0.7))
      const amp = 0.035 + 0.20 * uro + ekstra
      const w = 0.5 + 2.6 * uro
      for (let i = 0; i < N; i++) {
        const th = (i / N) * Math.PI * 2
        const k = 1
          + amp * (0.62 * Math.sin(FASE[i]! + t * w)
                 + 0.38 * Math.sin(FASE[i]! * 1.7 - t * w * 0.63))
        ut.push([48 + R * k * Math.cos(th), 48 + R * k * Math.sin(th)])
      }
      return ut
    }

    if (rolig) { el.setAttribute('d', kurve(punkt(0, 0))); return }

    let raf = 0
    const t0 = performance.now()
    const steg = (naa: number) => {
      const t = (naa - t0) / 1000

      /* Lyden, når det er noen. RMS i båndet laksen hører, glattet -- rå RMS
         hopper per bilde og ville fått formen til å flimre framfor å puste. */
      let live = 0
      const a = analyse.current
      const b = buf.current
      if (a && b && ph?.playing) {
        a.getFloatTimeDomainData(b)
        let s = 0
        for (let i = 0; i < b.length; i++) s += b[i]! * b[i]!
        const r = Math.sqrt(s / b.length)
        rms.current += (r - rms.current) * 0.15
        /* Opptakene ligger langt under fullskala, så skalaen er satt slik at et
           typisk hydrofonklipp bruker mesteparten av spennet. Dette er tekstur,
           ikke en måling, og den eneste påstanden den bærer er «nå skjer det
           noe» -- tallet står i spekteret ved siden av. */
        live = Math.min(0.18, rms.current * 24)
      } else {
        rms.current = 0
      }

      el.setAttribute('d', kurve(punkt(t, live)))
      raf = requestAnimationFrame(steg)
    }
    raf = requestAnimationFrame(steg)
    return () => cancelAnimationFrame(raf)
  }, [uro, ph])

  return (
    <div className={`blob ${klasse}`}>
      <svg viewBox="0 0 96 96" role="img"
           aria-label={avvik === null ? 'ikke målt'
             : avvik < 1 ? 'som vanlig i båndet laksen hører'
               : `${Math.round(avvik)} desibel over det vanlige i båndet laksen hører`}>
        {/* "Vanlig", som en stiplet sirkel. Uten den er blobben en form; med
            den er den en måling, fordi øyet får noe å måle mot. */}
        <circle className="blob-vanlig" cx="48" cy="48" r="30" />
        <path className="blob-form" ref={bane} />
      </svg>
      <span className="blob-tall">
        {avvik === null ? '–'
          : avvik < 1 ? 'som vanlig'
            : `+${Math.round(avvik)} dB`}
      </span>
    </div>
  )
}
